import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Gift, Globe2, PenLine, PlusSquare, RefreshCw, Shield, Smartphone, Sparkles, Store, UserPlus, Wallet, Zap } from 'lucide-react';
import { api } from '@/lib/api';
import type { Plan, Subscription } from '@/api/types';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { getWebApp, haptic } from '@/lib/telegram';
import { copyToClipboard } from '@/lib/clipboard';
import { formatWholeMinor, rub } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { Flag } from '@/components/ui/flag';
import { Group, MiniBar, QuickActions, Row, SectionTitle, StatTile, SubSwitcher, useFirstEnter, type QuickAction } from '@/components/ui/tiles';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Skeleton, SkeletonPage } from '@/components/ui/skeleton';
import { ExpiredCard } from './subscription';
import { SeasonGlyph } from '@/components/season/SeasonDetails';

/**
 * Главная (раскладка «Карта», 19.10.2026). Карта подписки, четыре быстрых действия,
 * сводка плитками и строки того, что бывает не всегда (розыгрыш, свой VPN). Всё, чем
 * подпиской управляют, живёт на вкладке «Подписка», деньги на вкладке «Кошелёк».
 */
export const Route = createFileRoute('/me')({ component: Dashboard });

export function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subs, sub, select, isPending } = useSelectedSubscription({ refetchOnMount: 'always' });

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: expiredSubs } = useQuery({
    queryKey: ['subs-expired'],
    queryFn: () => api.expiredSubscriptions(),
    enabled: !isPending && !sub,
    staleTime: 60_000,
  });

  if (isPending && !sub) return <HomeSkeleton />;
  if (!sub) return <EmptyHome expired={expiredSubs?.[0] ?? null} />;

  const lifetime = sub.supplier === 'scvpn';
  const actions: QuickAction[] = [
    // Без ссылки подключать и копировать нечего: таких действий нет вовсе.
    ...(sub.configUrl
      ? [
          {
            key: 'connect',
            primary: true,
            icon: <Zap />,
            label: t('Connect short'),
            onClick: () => navigate({ to: '/success/$subscriptionId', params: { subscriptionId: sub.id } }),
          },
          {
            key: 'link',
            icon: <Copy />,
            label: t('Link short'),
            onClick: async () => {
              if (await copyToClipboard(sub.configUrl)) {
                haptic('success');
                toast.success(t('Link copied'));
              } else haptic('error');
            },
          },
        ]
      : []),
    // Пробник проверяем раньше раздела: у пробника «Навсегда» докупок нет, «Докупить»
    // вело бы на вкладку, где нечего купить.
    sub.isTrial
      ? {
          key: 'plans',
          icon: <Shield />,
          label: t('Choose plan'),
          onClick: () => navigate({ to: '/plans', search: lifetime ? { section: 'lifetime' } : {} }),
        }
      : lifetime
        ? { key: 'more', icon: <PlusSquare />, label: t('Buy more short'), onClick: () => navigate({ to: '/subscription' }) }
        : {
            key: 'renew',
            icon: <RefreshCw />,
            label: t('Renew'),
            onClick: () => navigate({ to: '/renew/$subscriptionId', params: { subscriptionId: sub.id } }),
          },
    { key: 'gift', icon: <Gift />, label: t('Gift'), onClick: () => navigate({ to: '/gift' }) },
  ];

  return (
    <div className="space-y-4 p-4 pb-6">
      <Greeting />
      <SubSwitcher items={subs.map((s) => ({ id: s.id, label: s.planName }))} value={sub.id} onChange={select} />
      <SubscriptionCard sub={sub} onClick={() => navigate({ to: '/subscription' })} />
      <div className="pb-2 pt-1">
        <QuickActions items={actions} />
      </div>
      <Overview sub={sub} />
      <Extras sub={sub} />
    </div>
  );
}

/* ---------- Шапка ---------- */

function Greeting() {
  const { t } = useTranslation();
  const name = getWebApp()?.initDataUnsafe?.user?.first_name?.trim();
  return (
    <header className="tl-title flex items-end justify-between gap-3 px-1 pt-2">
      <h1 className="flex min-w-0 items-center gap-2 text-[24px] font-semibold tracking-[-0.025em]">
        <span className="truncate">{name ? t('Hello name', { name }) : t('Hello')}</span>
        <SeasonGlyph />
      </h1>
    </header>
  );
}

/* ---------- Сводка ---------- */

const gb = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: n < 10 ? 1 : 0 });

function Overview({ sub }: { sub: Subscription }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const section = sub.supplier === 'scvpn' ? 'lifetime' : 'term';
  const { data: loc, isPending: locPending } = useQuery({
    queryKey: ['client-locations', section],
    queryFn: () => api.locations(section),
    staleTime: 5 * 60 * 1000,
  });
  // Серверов нет (сеть раздела не работает): плитка, ведущая в пустой список, не нужна.
  const showLocations = locPending || (loc?.total ?? 0) > 0;
  return (
    <section className="pt-2">
      <SectionTitle>{t('Summary')}</SectionTitle>
      <div className="tl-grid grid grid-cols-2 gap-2.5">
        <TrafficTile sub={sub} onOpen={() => navigate({ to: '/subscription' })} />
        <DevicesTile sub={sub} onOpen={() => navigate({ to: '/devices' })} />
        {showLocations && (
          <StatTile
            icon={<Globe2 />}
            label={t('Locations')}
            value={locPending ? <Skeleton className="h-7 w-10 rounded-md" /> : loc?.total ?? 0}
            unit={locPending ? undefined : t('Countries word', { count: loc?.total ?? 0 })}
            onClick={() => navigate({ to: '/locations', search: { section } })}
          >
            {!locPending && <FlagStack codes={Object.values(loc?.regions ?? {}).flat().map((l) => l.country_code)} />}
          </StatTile>
        )}
        <FriendsTile wide={!showLocations} />
      </div>
    </section>
  );
}

function FlagStack({ codes }: { codes: (string | null | undefined)[] }) {
  const list = [...new Set(codes.filter((c): c is string => !!c))].slice(0, 5);
  if (!list.length) return null;
  return (
    <>
      <span className="tl-flags mt-3 flex" aria-hidden>
        {list.map((c) => (
          <span key={c} className="-mr-2 grid size-[26px] place-items-center overflow-hidden rounded-full border-2 border-[var(--t-surface)] bg-[var(--t-surface-2)]">
            <Flag flag="" code={c} square className="size-full rounded-none align-top" />
          </span>
        ))}
      </span>
      {/* Коды стран вместо флагов: только в «Схеме». */}
      <span className="tl-codes mt-3 hidden gap-1" aria-hidden>
        {list.slice(0, 4).map((c) => (
          <span key={c} className="border border-[var(--t-line)] px-[5px] py-[2px] text-[11px] font-medium text-[var(--t-body)] tabular">
            {c.toUpperCase()}
          </span>
        ))}
        {list.length > 4 && <span className="border border-[var(--t-line)] px-[5px] py-[2px] text-[11px] text-[var(--t-body)] tabular">+{list.length - 4}</span>}
      </span>
    </>
  );
}

function TrafficTile({ sub, onOpen }: { sub: Subscription; onOpen: () => void }) {
  const { t } = useTranslation();
  const total = sub.trafficTotalGb;
  const left = total === null ? null : Math.max(0, total - sub.trafficUsedGb);
  return (
    <StatTile
      icon={<Zap />}
      label={t('Bypass')}
      value={left === null ? '∞' : gb(left)}
      unit={left === null ? undefined : t('GB')}
      hint={total === null ? t('No limit') : t('Of total gb', { total: gb(total) })}
      onClick={onOpen}
    >
      <MiniBar value={total ? (left ?? 0) / total : 1} />
    </StatTile>
  );
}

function DevicesTile({ sub, onOpen }: { sub: Subscription; onOpen: () => void }) {
  const { t } = useTranslation();
  // У «Навсегда» список стоит два запроса к поставщику: на главной его не грузим.
  const lifetime = sub.supplier === 'scvpn';
  const { data: devices } = useQuery({
    queryKey: ['devices', sub.id],
    queryFn: () => api.devices(sub.id),
    enabled: !lifetime,
    placeholderData: keepPreviousData,
  });
  const used = devices?.length ?? null;
  const max = sub.devices;
  if (lifetime || used === null) {
    return (
      <StatTile icon={<Smartphone />} label={t('Devices')} value={max} unit={t('Devices unit', { count: max })} hint={t('At the same time')} onClick={onOpen} />
    );
  }
  return (
    <StatTile icon={<Smartphone />} label={t('Devices')} value={used} unit={t('of n', { count: max })} hint={t('Connected now')} onClick={onOpen}>
      {max <= 8 ? (
        <span className="mt-3 flex gap-1">
          {Array.from({ length: max }).map((_, i) => (
            <span key={i} className={cn('h-1 flex-1 rounded-full', i < used ? 'bg-foreground' : 'bg-[var(--t-fill-strong)]')} />
          ))}
        </span>
      ) : (
        <MiniBar value={used / max} />
      )}
    </StatTile>
  );
}

function FriendsTile({ wide }: { wide?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile, isPending } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile(),
    placeholderData: keepPreviousData,
  });
  const span = wide ? 'col-span-2' : undefined;
  if (isPending && !profile) return <Skeleton className={cn('h-[132px] rounded-[var(--r-card)]', span)} />;
  // Программу выключил партнёр: плитка не обещает бонус, которого не будет.
  if (!profile?.referral_enabled) {
    return (
      <StatTile
        className={span}
        icon={<Wallet />}
        label={t('Balance')}
        value={formatWholeMinor(profile?.balance_display_minor ?? 0, profile?.display_currency ?? 'RUB')}
        hint={t('Top up')}
        onClick={() => navigate({ to: '/wallet' })}
      />
    );
  }
  return (
    <StatTile
      className={span}
      icon={<UserPlus />}
      label={t('Friends')}
      value={`${profile.referral_l1_percent ?? 15}%`}
      hint={t('From their payments')}
      onClick={() => navigate({ to: '/wallet' })}
    />
  );
}

/* ---------- Что бывает не всегда ---------- */

/** Розыгрыш и «Свой VPN» строками. Нет ни того, ни другого: блока нет. */
function Extras({ sub }: { sub: Subscription }) {
  const giveaway = useGiveawayRow();
  const review = useReviewRow(sub);
  const mirror = useMirrorRow();
  if (!giveaway && !review && !mirror) return null;
  return (
    <Group className="pt-2">
      {giveaway}
      {review}
      {mirror}
    </Group>
  );
}

/** Отзыв за оплаченную подписку в любой момент, а не только по напоминанию через пять
 *  дней. Пробник на витрину не пускаем: она подписана «только подтверждённые подписки». */
function useReviewRow(sub: Subscription) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!api.getAuth().reviewsEnabled || sub.isTrial) return null;
  return (
    <Row
      icon={<PenLine />}
      title={t('Reviews leave')}
      hint={t('Review tile hint')}
      onClick={() => navigate({ to: '/reviews', search: { leave: sub.id } })}
    />
  );
}

function useGiveawayRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['giveaways'], queryFn: () => api.giveaways(), staleTime: 60_000 });
  const list = data ?? [];
  if (list.length === 0) return null;
  const ready = list.some((g) => g.can_join);
  return (
    <Row
      icon={<Sparkles />}
      title={<span className="block truncate">{list.length > 1 ? t('Giveaways count', { count: list.length }) : list[0].title}</span>}
      hint={ready ? t('Giveaway tile ready') : t('Giveaway tile joined')}
      onClick={() => navigate({ to: '/giveaways' })}
    />
  );
}

/** «Свой VPN»: создание живёт в чате, строка открывает его диплинком. */
function useMirrorRow() {
  const { t } = useTranslation();
  const { data: info } = useQuery({ queryKey: ['info'], queryFn: () => api.info(), staleTime: 300_000 });
  if (!api.getAuth().mirrorsEnabled || !info?.bot_username) return null;
  const open = () => {
    haptic('light');
    const tg = getWebApp();
    const url = `https://t.me/${info.bot_username}?start=open_mirror`;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
    tg?.close();
  };
  return <Row icon={<Store />} title={t('Own VPN')} hint={<span className="line-clamp-2">{t('Own VPN hint')}</span>} onClick={open} />;
}

/* ---------- Без подписки ---------- */

function EmptyHome({ expired }: { expired: (Subscription & { renewable: boolean }) | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: trial } = useQuery({ queryKey: ['trial-status'], queryFn: () => api.trialStatus(), staleTime: 60_000 });
  const { data: plans } = useQuery({ queryKey: ['plans', 'term'], queryFn: () => api.plans('term'), staleTime: 60_000 });

  // Самая низкая цена месяца у каждого семейства (по числу устройств): «от 116 ₽».
  const families = new Map<number, { name: string; perMonth: number }>();
  for (const p of (plans ?? []) as Plan[]) {
    if (p.isSpecial || !p.days) continue;
    const perMonth = Math.round((p.priceRub / p.days) * 30);
    const cur = families.get(p.devices);
    if (!cur || perMonth < cur.perMonth) families.set(p.devices, { name: p.name, perMonth });
  }
  const top = [...families.entries()].sort((a, b) => a[0] - b[0]).slice(0, 2);
  const toPlans = () => navigate({ to: '/plans' });
  const giveaway = useGiveawayRow();
  const mirror = useMirrorRow();

  return (
    <div className="space-y-4 p-4 pb-6">
      <Greeting />
      {expired && <ExpiredCard sub={expired} />}

      {/* Карта-приглашение: тот же материал, что у карты подписки, чтобы новичок с
          первого экрана видел, как будет выглядеть его подписка. */}
      <PromoCard
        kicker={trial?.available ? t('Trial days free', { count: trial.days }) : t('Home hero desc')}
        title={trial?.available ? t('Try VPN free') : t('Home hero title')}
        action={trial?.available ? t('Start trial') : t('Open plans')}
        onClick={toPlans}
      />

      {top.length > 0 && (
        <section className="pt-2">
          <SectionTitle action={t('All plans')} onAction={toPlans}>
            {t('Plans title')}
          </SectionTitle>
          <div className="tl-grid grid grid-cols-2 gap-2.5">
            {top.map(([devices, f]) => (
              <StatTile
                key={devices}
                icon={<Shield />}
                label={f.name}
                value={t('From price', { price: rub(f.perMonth) })}
                hint={`${t('Per month')}, ${t('Devices word', { count: devices })}`}
                onClick={toPlans}
              />
            ))}
          </div>
        </section>
      )}

      {(giveaway || mirror) && (
        <Group>
          {giveaway}
          {mirror}
        </Group>
      )}

      <Group title={t('How to connect')} className="pt-2">
        {[t('How step 1'), t('How step 2'), t('How step 3')].map((text, i) => (
          <Row key={i} icon={<span className="text-[14px] font-semibold tabular">{i + 1}</span>} title={text} />
        ))}
      </Group>
    </div>
  );
}

function PromoCard({ kicker, title, action, onClick }: { kicker: string; title: string; action: string; onClick: () => void }) {
  const enter = useFirstEnter();
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light');
        onClick();
      }}
      className={`${enter ? 'enter-soft ' : ''}relative flex min-h-[200px] w-full flex-col justify-between overflow-hidden rounded-[var(--r-subcard)] p-5 text-left text-[var(--card-fg)] shadow-[inset_0_0_0_1px_var(--card-line),var(--card-shadow)] transition-transform duration-100 active:scale-[0.98]`}
      style={{ background: 'var(--card-bg)' }}
    >
      <span className="relative block">
        <span className="block text-[13px] opacity-75">{kicker}</span>
        <span className="mt-2 block text-[28px] font-semibold leading-[1.08] tracking-[-0.03em]">{title}</span>
      </span>
      <span className="tl-promo-cta relative mt-6 inline-flex h-11 items-center self-start rounded-[var(--r-btn)] bg-[var(--card-fg)] px-5 text-[15px] font-semibold text-primary">
        {action}
      </span>
    </button>
  );
}

function HomeSkeleton() {
  return (
    <SkeletonPage className="space-y-4 p-4">
      <Skeleton className="mx-1 mt-2 h-7 w-48 rounded-lg" />
      <Skeleton className="aspect-[1.586] w-full rounded-[var(--r-subcard)]" />
      <div className="tl-sk-actions grid grid-cols-4 gap-2 pb-2 pt-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <Skeleton className="tl-sk-act size-[58px] rounded-full" />
            <Skeleton className="tl-sk-label h-3 w-14 rounded" />
          </div>
        ))}
      </div>
      <Skeleton className="mx-1 mt-2 h-6 w-24 rounded-lg" />
      <div className="grid grid-cols-2 gap-2.5">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[132px] rounded-[var(--r-card)]" />
        ))}
      </div>
    </SkeletonPage>
  );
}
